import { useCallback, useEffect, useState } from 'react';
import type { PayCycle, PrepareRow, PrepareView, RunDetail, RunRow, RunSummary } from '../lib/payruns';
import { handedRs, payrunApi, shortRs } from '../lib/payruns';
import { ApiError } from '../lib/api';
import { openPrint } from '../lib/print';
import { formatDate, formatHours, formatRs, rupeesInWords } from '../lib/money';
import SignDialog from '../components/SignDialog';
import { ReviewRun } from '../components/PayRunApprovals';
import PayRunPack, { Payslips } from '../print/PayRunPack';
import { ReceiptSheets } from '../print/AdvanceReceipt';
import type { AdvanceRow, EntryRow } from '../lib/day';
import Funds from './Funds';
import WorkerRecords from './WorkerRecords';
import Loans from './Loans';
import { loanApi, loanState } from '../lib/loans';

/**
 * Pay runs — "two sections: Prepare a pay run, Pay run history", and Loans
 * beside them (25 September 2026), whose weekly repayments every run takes.
 *
 * The clerk reaches this through a segment inside her Labour tab; the owner has
 * it as a tab of his own and is the only one who can approve a run.
 */

interface Props {
  isOwner: boolean;
  userName: string;
  /** Who is signed in — stamped as "Checked by" on a statement they print. */
  userId: string;
  today: string;
}

type Tab = 'weekly' | 'monthly' | 'history' | 'loans';

export default function PayRun({ isOwner, userName, userId, today }: Props) {
  // Weekly pay and monthly pay are tabs of their own (25 September 2026:
  // "in the pay run can we have 2 section. 1 for weekly pay another tab for
  // monthly pay").
  const [tab, setTab] = useState<Tab>('weekly');
  // Loans waiting for the owner, counted on the Loans segment.
  const [loansWaiting, setLoansWaiting] = useState(0);
  useEffect(() => {
    if (!isOwner) return;
    loanApi.list()
      .then((result) => setLoansWaiting(result.loans.filter((loan) => loanState(loan) === 'waiting').length))
      .catch(() => { /* the Loans segment reports its own error */ });
  }, [isOwner]);
  const [runCount, setRunCount] = useState(0);
  // Runs waiting for the owner. Approve lives on the history segment, as in
  // the prototype, and the tab opens on Prepare — so the owner opened his
  // Pay run tab and found no way to approve (23 September 2026). When a run
  // is waiting, his tab opens on the runs; the Prepare segment says so too.
  const [waiting, setWaiting] = useState(0);
  const [chosen, setChosen] = useState(false);
  useEffect(() => {
    if (!isOwner) return;
    let live = true;
    payrunApi.list()
      .then((result) => {
        if (!live) return;
        const count = result.runs.filter((run) => run.status === 'unapproved').length;
        setWaiting(count);
        if (count > 0 && !chosen) setTab('history');
      })
      .catch(() => { /* the history card reports its own error */ });
    return () => { live = false; };
  }, [isOwner, chosen]);
  const choose = (next: Tab) => { setChosen(true); setTab(next); };

  return (
    <>
      <div className="seg wide">
        <button
          type="button"
          className={tab === 'weekly' ? 'on' : ''}
          onClick={() => choose('weekly')}
        >
          Weekly pay
        </button>
        <button
          type="button"
          className={tab === 'monthly' ? 'on' : ''}
          onClick={() => choose('monthly')}
        >
          Monthly pay
        </button>
        <button
          type="button"
          className={tab === 'history' ? 'on' : ''}
          onClick={() => choose('history')}
        >
          Pay run history
          {runCount > 0 && <span className="cnt">{runCount}</span>}
        </button>
        <button
          type="button"
          className={tab === 'loans' ? 'on' : ''}
          onClick={() => choose('loans')}
        >
          Loans
          {isOwner && loansWaiting > 0 && <span className="cnt">{loansWaiting}</span>}
        </button>
      </div>

      {(tab === 'weekly' || tab === 'monthly') && isOwner && waiting > 0 && (
        <div className="banner b-warn" style={{ justifyContent: 'space-between' }}>
          <span>
            {waiting} pay run{waiting > 1 ? 's' : ''} waiting for your approval.
          </span>
          <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => choose('history')}>
            Approve ›
          </button>
        </div>
      )}
      {tab === 'loans' ? (
        <Loans isOwner={isOwner} today={today} onWaiting={setLoansWaiting} />
      ) : tab === 'weekly' || tab === 'monthly' ? (
        <Prepare key={tab} cycle={tab} onCreated={() => choose('history')} />
      ) : (
        <>
          <History
            isOwner={isOwner}
            userName={userName}
            onCount={setRunCount}
            onWaiting={setWaiting}
          />
          {/* The fund card is the owner's alone, whatever `payrun` allows. */}
          {isOwner && <Funds userName={userName} today={today} />}
          <WorkerRecords userName={userName} userId={userId} />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------- prepare

/** 'YYYY-MM' → its first and last day. */
function monthBounds(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

function Prepare({ cycle, onCreated }: { cycle: PayCycle; onCreated: () => void }) {
  const [view, setView] = useState<PrepareView | null>(null);
  // The pay day for this run: the usual one unless moved for a public holiday.
  const [payOn, setPayOn] = useState<string | null>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await payrunApi.prepare(cycle, range?.from, range?.to);
      setView(result);
      setPayOn(result.payOn);
      if (!range) setRange({ from: result.from, to: result.to });
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not work out the pay run.');
    }
  }, [range, cycle]);

  useEffect(() => { void load(); }, [load]);

  if (!view) {
    return (
      <div className="card">
        <h3>{cycle === 'weekly' ? 'Weekly pay' : 'Monthly pay'}</h3>
        <div className="empty">{error || 'Loading…'}</div>
      </div>
    );
  }
  const usualPayOn = view.payOn;
  const moved = payOn !== null && usualPayOn !== null && payOn !== usualPayOn;

  const payable = view.rows.filter((row) => row.blockedSince === null);
  const selected = payable.filter((row) => !skipped.has(row.workerId));
  const blocked = view.rows.filter((row) => row.blockedSince !== null);
  const earliestBlocked = blocked.reduce<string | null>(
    (earliest, row) =>
      !earliest || row.blockedSince! < earliest ? row.blockedSince! : earliest,
    null,
  );

  const totals = selected.reduce(
    (acc, row) => ({
      net: acc.net + handedRs(row.takeHomeRs),
      loan: acc.loan + row.loanRs,
      epfEmp: acc.epfEmp + row.epfEmployeeRs,
      epfEr: acc.epfEr + row.epfEmployerRs,
      etf: acc.etf + row.etfRs,
    }),
    { net: 0, loan: 0, epfEmp: 0, epfEr: 0, etf: 0 },
  );

  async function create() {
    setBusy(true);
    setError('');
    try {
      await payrunApi.create(cycle, view!.from, view!.to, selected.map((row) => row.workerId), moved ? payOn! : undefined);
      setSkipped(new Set());
      onCreated();
    } catch (err) {
      if (err instanceof ApiError && typeof err.detail?.startFrom === 'string') {
        setRange({ from: err.detail.startFrom, to: view!.to });
      }
      setError(err instanceof ApiError ? err.message : 'Could not create the pay run.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3>{cycle === 'weekly' ? 'Weekly pay' : 'Monthly pay'}</h3>
      <div className="sub">
        {cycle === 'weekly'
          ? 'Employees paid weekly, on Saturday for Monday to Saturday. Check the figures, create it, print an unapproved copy for the owner. A member\'s EPF/ETF is on his weekly EPF salary for each week he worked.'
          : 'Employees paid monthly, for the month before. A member\'s EPF/ETF is on his monthly EPF salary for each month he worked.'}
      </div>

      {cycle === 'weekly' ? (
        <div className="bar" style={{ marginTop: 10 }}>
          <div>
            <div className="sub">From</div>
            <input
              type="date"
              value={view.from}
              onChange={(e) => setRange({ from: e.target.value, to: view.to })}
            />
          </div>
          <div>
            <div className="sub">To</div>
            <input
              type="date"
              value={view.to}
              onChange={(e) => setRange({ from: view.from, to: e.target.value })}
            />
          </div>
        </div>
      ) : (
        <div className="bar" style={{ marginTop: 10, alignItems: 'flex-end' }}>
          <div>
            <div className="sub">Month</div>
            <input
              type="month"
              value={view.from.slice(0, 7)}
              onChange={(e) => { if (e.target.value) setRange(monthBounds(e.target.value)); }}
            />
          </div>
          <div className="sub" style={{ paddingBottom: 10 }}>
            {formatDate(view.from)} – {formatDate(view.to)}
          </div>
        </div>
      )}

      {/* The usual pay day, which can be moved for a public holiday. */}
      <div className="fld" style={{ marginTop: 8 }}>
        <div>
          Pay day
          <div className="sub">
            {moved
              ? <>Moved from {formatDate(usualPayOn!)} — a public holiday? It prints on the payslips.</>
              : cycle === 'weekly'
                ? 'The Saturday. Change it if the pay goes out on another day, such as before a public holiday.'
                : 'The monthly pay day from Settings. Change it for this run if that day is a public holiday.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="date"
            value={payOn ?? ''}
            min={view.from}
            onChange={(e) => setPayOn(e.target.value || usualPayOn)}
            style={{ width: 170 }}
          />
          {moved && (
            <button type="button" className="btn sm" onClick={() => setPayOn(usualPayOn)}>
              Usual day
            </button>
          )}
        </div>
      </div>

      {/* Unapproved days stop the whole run — nothing can be paid out of them. */}
      {view.unapprovedDays.length > 0 && (
        <div className="banner b-warn">
          {view.unapprovedDays.length} day{view.unapprovedDays.length > 1 ? 's' : ''} not
          approved yet ({view.unapprovedDays.map((d) => formatDate(d)).join(', ')}). Pay cannot
          be run until the owner approves them.
        </div>
      )}

      {view.alreadyPaid.length > 0 && (
        <div className="banner b-info">
          Already paid in an earlier run, so left out:{' '}
          {view.alreadyPaid.map((row) => `${row.workerName} (${row.days} d)`).join(', ')}
        </div>
      )}

      {/* Pay has to go in order — but only for the men it applies to. Everyone
          else can still be paid on time. */}
      {blocked.length > 0 && (
        <div className="banner b-warn" style={{ justifyContent: 'space-between' }}>
          <span>
            Pay has to go in order. {blocked.length} employee{blocked.length > 1 ? 's have' : ' has'}{' '}
            unpaid days before {formatDate(view.from)}, so they cannot be paid for this week yet.
          </span>
          {earliestBlocked && (
            <button
              type="button"
              className="btn sm"
              onClick={() => setRange({ from: earliestBlocked, to: view.to })}
            >
              Start from {formatDate(earliestBlocked)}
            </button>
          )}
        </div>
      )}

      {error && <div className="banner b-no">{error}</div>}

      <div className="bar" style={{ margin: '10px 0 4px' }}>
        <button type="button" className="btn sm" onClick={() => setSkipped(new Set())}>
          Select everyone
        </button>
        <button
          type="button"
          className="btn sm"
          onClick={() => setSkipped(new Set(payable.map((row) => row.workerId)))}
        >
          Clear
        </button>
        <span className="sub" style={{ alignSelf: 'center', textAlign: 'right' }}>
          {selected.length} of {view.rows.length} selected
        </span>
      </div>

      {view.rows.length === 0 && <div className="empty">Nothing left to pay in this range.</div>}

      {view.rows.map((row) => (
        <WorkerRow
          key={row.workerId}
          row={row}
          rates={view.rates}
          cycle={cycle}
          picked={row.blockedSince === null && !skipped.has(row.workerId)}
          open={openRow === row.workerId}
          onToggle={() =>
            setSkipped((current) => {
              const next = new Set(current);
              if (next.has(row.workerId)) next.delete(row.workerId);
              else next.add(row.workerId);
              return next;
            })
          }
          onOpen={() => setOpenRow(openRow === row.workerId ? null : row.workerId)}
        />
      ))}

      <div className="ln tot">
        <span>Cash to pay the employees ({selected.length})</span>
        <span>{formatRs(totals.net)}</span>
      </div>
      {totals.loan > 0 && (
        <div className="ln">
          <span className="k">Loan repayments kept back (already taken off above)</span>
          <span>{formatRs(totals.loan)}</span>
        </div>
      )}
      {(totals.epfEmp > 0 || totals.epfEr > 0 || totals.etf > 0) && (
        <>
          <div className="ln">
            <span className="k">
              EPF employee {view.rates.epfEmployeePctX100 / 100}% withheld
            </span>
            <span>{formatRs(totals.epfEmp)}</span>
          </div>
          <div className="ln">
            <span className="k">
              Employer EPF {view.rates.epfEmployerPctX100 / 100}% + ETF{' '}
              {view.rates.etfPctX100 / 100}%
            </span>
            <span>{formatRs(totals.epfEr + totals.etf)}</span>
          </div>
          <div className="ln" style={{ fontWeight: 500 }}>
            <span>Due to EPF/ETF for this run</span>
            <span>{formatRs(totals.epfEmp + totals.epfEr + totals.etf)}</span>
          </div>
        </>
      )}

      <div className="bar" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn pri"
          disabled={busy || selected.length === 0 || view.unapprovedDays.length > 0}
          onClick={() => void create()}
        >
          Create pay run for {selected.length} employee{selected.length === 1 ? '' : 's'}
        </button>
      </div>
    </div>
  );
}

function WorkerRow({
  row, rates, cycle, picked, open, onToggle, onOpen,
}: {
  row: PrepareRow;
  rates: PrepareView['rates'];
  cycle: PayCycle;
  picked: boolean;
  open: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const blocked = row.blockedSince !== null;

  return (
    <>
      <div className="row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          {blocked ? (
            <button type="button" className="btn sm" disabled style={{ minWidth: 64 }}>
              Blocked
            </button>
          ) : (
            <button
              type="button"
              className={`btn sm${picked ? ' pri' : ''}`}
              style={{ minWidth: 64 }}
              onClick={onToggle}
            >
              {picked ? 'Pay' : 'Skip'}
            </button>
          )}
          <div
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={onOpen}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(); }}
          >
            <div className="name" style={blocked ? { color: 'var(--text-3)' } : undefined}>
              {row.workerName}
            </div>
            <div className="sub">
              {blocked ? (
                <span style={{ color: 'var(--text-warning)' }}>
                  unpaid since {formatDate(row.blockedSince!)} — pay that first
                </span>
              ) : (
                <>
                  {row.daysWorked} days
                  {row.otTenths > 0 && ` · OT ${formatHours(row.otTenths)}h`}
                  {row.allowanceRs > 0 && ` · allow. ${formatRs(row.allowanceRs)}`}
                  {row.advancesRs > 0 && ` · advances ${formatRs(row.advancesRs)}`}
                  {row.carriedInRs > 0 && ` · short from last pay −${formatRs(row.carriedInRs)}`}
                  {row.loanRs > 0 && ` · loan −${formatRs(row.loanRs)}`}
                  {row.epfEmployeeRs > 0
                    ? ` · EPF −${formatRs(row.epfEmployeeRs)} on ${formatRs(row.epfBasisRs)}`
                    : row.isEpfMember
                      ? ''
                      : ' · no EPF'}
                  {' · tap for detail'}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="stat">
          <div className="name" style={blocked ? { color: 'var(--text-3)' } : undefined}>
            {formatRs(handedRs(row.takeHomeRs))}
          </div>
          <div className="sub">
            {shortRs(row.takeHomeRs) > 0
              ? <span style={{ color: 'var(--text-warning)' }}>short {formatRs(shortRs(row.takeHomeRs))} — to next pay</span>
              : `${formatRs(row.grossRs)} gross`}
          </div>
        </div>
      </div>

      {open && (
        <div
          style={{
            background: 'var(--surface-2)', borderRadius: 12,
            padding: '10px 12px', margin: '0 -4px 8px',
          }}
        >
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Job</th>
                  <th className="num">Hrs</th>
                  <th className="num">OT</th>
                  <th className="num">Allow.</th>
                  <th className="num">Earned</th>
                </tr>
              </thead>
              <tbody>
                {row.days.map((day) => (
                  <tr key={day.workDate}>
                    <td>{formatDate(day.workDate)}</td>
                    <td>
                      {day.roleName ?? '—'}
                      {day.payBasis === 'trip' && ' (allowance only)'}
                    </td>
                    <td className="num">
                      {day.payBasis === 'trip' ? '—' : formatHours(day.hoursTenths)}
                    </td>
                    <td className="num">{day.otTenths ? formatHours(day.otTenths) : '—'}</td>
                    <td className="num">
                      {day.allowanceRs + day.sundayAllowanceRs
                        ? formatRs(day.allowanceRs + day.sundayAllowanceRs)
                        : '—'}
                    </td>
                    <td className="num">{formatRs(day.earnedRs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {row.advances.length > 0 && (
            <>
              <div className="sub" style={{ marginTop: 8 }}>Advances taken</div>
              {row.advances.map((advance) => (
                <div className="ln" key={advance.id}>
                  <span className="k">
                    {formatDate(advance.workDate)}
                    {advance.note && ` · ${advance.note}`}
                  </span>
                  <span style={{ color: 'var(--text-danger)' }}>
                    −{formatRs(advance.amountRs)}
                  </span>
                </div>
              ))}
            </>
          )}

          <div className="ln" style={{ marginTop: 8 }}>
            <span className="k">Gross earnings</span>
            <span>{formatRs(row.grossRs)}</span>
          </div>
          {row.advancesRs > 0 && (
            <div className="ln">
              <span className="k">Less advances</span>
              <span style={{ color: 'var(--text-danger)' }}>−{formatRs(row.advancesRs)}</span>
            </div>
          )}
          {/* Hidden entirely for a non-member, and for a member with nothing in
              the period. Never rows of zeros. */}
          {row.isEpfMember && row.baseEarnedRs > 0 ? (
            <>
              <div className="ln">
                <span className="k">Base salary earned</span>
                <span>{formatRs(row.baseEarnedRs)}</span>
              </div>
              {row.epfSalaryRs !== null && (
                <div className="ln">
                  <span className="k">
                    EPF/ETF is on the EPF salary — {formatRs(row.epfSalaryRs)} a{' '}
                    {cycle === 'monthly' ? 'month' : 'week'}, once for each {cycle === 'monthly' ? 'month' : 'week'} he worked
                  </span>
                  <span>{formatRs(row.epfBasisRs)}</span>
                </div>
              )}
              <div className="ln">
                <span className="k">EPF employee {rates.epfEmployeePctX100 / 100}%</span>
                <span style={{ color: 'var(--text-danger)' }}>
                  −{formatRs(row.epfEmployeeRs)}
                </span>
              </div>
              <div className="ln">
                <span className="k">
                  Employer EPF {rates.epfEmployerPctX100 / 100}% + ETF {rates.etfPctX100 / 100}%
                  (not deducted)
                </span>
                <span>{formatRs(row.epfEmployerRs + row.etfRs)}</span>
              </div>
            </>
          ) : (
            <div className="ln">
              <span className="k">Not an EPF/ETF member</span>
              <span>—</span>
            </div>
          )}
          {row.carriedInRs > 0 && (
            <div className="ln">
              <span className="k">Short from last pay — advances were more than that pay</span>
              <span style={{ color: 'var(--text-danger)' }}>−{formatRs(row.carriedInRs)}</span>
            </div>
          )}
          {row.loans.map((loan) => (
            <div className="ln" key={loan.loanId}>
              <span className="k">
                Loan repayment — {formatRs(loan.loanAmountRs)} given {formatDate(loan.givenOn)}
                {' · '}{formatRs(loan.balanceAfterRs)} still owed after this
              </span>
              <span style={{ color: 'var(--text-danger)' }}>−{formatRs(loan.amountRs)}</span>
            </div>
          ))}
          <div className="ln tot">
            <span>Net pay</span>
            <span>{formatRs(handedRs(row.takeHomeRs))}</span>
          </div>
          {shortRs(row.takeHomeRs) > 0 && (
            <div className="ln">
              <span className="k">Short this pay — carried to his next pay</span>
              <span style={{ color: 'var(--text-warning)' }}>{formatRs(shortRs(row.takeHomeRs))}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------- history

function History({
  isOwner, userName, onCount, onWaiting,
}: {
  isOwner: boolean;
  onWaiting: (count: number) => void;
  userName: string;
  onCount: (n: number) => void;
}) {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  // The approved run whose pay is being handed out, one employee at a time.
  const [handing, setHanding] = useState<string | null>(null);
  // The unapproved run whose summary the owner is reading before approving.
  const [reviewing, setReviewing] = useState<string | null>(null);
  // The earlier, finished runs: folded until the arrow is tapped.
  const [showEarlier, setShowEarlier] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await payrunApi.list();
      setRuns(result.runs);
      onCount(result.runs.length);
      onWaiting(result.runs.filter((run) => run.status === 'unapproved').length);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the pay runs.');
    }
  }, [onCount, onWaiting]);

  useEffect(() => { void load(); }, [load]);

  async function act(id: string, work: () => Promise<unknown>) {
    setBusy(id);
    setError('');
    try {
      await work();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  async function print(id: string, payslipsOnly: boolean) {
    setBusy(id);
    try {
      const detail = await payrunApi.get(id);
      const label = `${detail.run.status === 'approved' ? 'Approved' : 'Unapproved'} pay run — ${
        payslipsOnly ? `${detail.rows.length} payslips` : `summary + ${detail.rows.length} payslips`
      }`;
      openPrint(
        label,
        payslipsOnly ? (
          <Payslips detail={detail} />
        ) : (
          <PayRunPack detail={detail} printedBy={userName} printedAt={new Date()} />
        ),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that pay run.');
    } finally {
      setBusy(null);
    }
  }

  /** Every signed docket in the run, as one printable set — "in case I need it". */
  async function printReceipts(id: string) {
    setError('');
    setBusy(id);
    try {
      const detail = await payrunApi.get(id);
      if (detail.advances.length === 0) {
        setError('No advances were taken in that pay run, so there are no receipts.');
        return;
      }
      const items = detail.advances.map((advance) => ({
        workDate: advance.work_date,
        // the receipt reads three things off the day's entry
        entry: {
          workerName: advance.worker_name,
          rolesLabel: advance.role_name ?? '',
          advancesThisWeekRs: advance.week_rs,
        } as unknown as EntryRow,
        advance: {
          id: advance.advance_id,
          amountRs: advance.amount_rs,
          note: advance.note,
          payWeekEnding: advance.pay_week_ending,
          signed: !!advance.signed,
          signedAt: advance.signed_at,
          paidByName: advance.paid_by_name,
          rowVersion: 0,
        } as AdvanceRow,
        // "Approved" on the receipt: whoever approved that day's labour.
        approval: { by: advance.approved_by, name: advance.approved_by_name, at: advance.approved_at },
      }));
      openPrint(
        `${items.length} advance receipt${items.length === 1 ? '' : 's'} — ${formatDate(detail.run.period_from)} to ${formatDate(detail.run.period_to)}`,
        <ReceiptSheets items={items} workDate={detail.run.period_to} />,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open those receipts.');
    } finally {
      setBusy(null);
    }
  }

  // Finished runs fold away (26 September 2026: "once the pay run completed
  // previous weeks can be hidden and open once click an arrow"). Runs still
  // waiting for the owner stay open, and so does the latest approved weekly
  // and monthly run — the ones being handed out. The rest sit behind the arrow.
  const latestWeekly = runs.find((run) => run.status === 'approved' && run.cycle !== 'monthly');
  const latestMonthly = runs.find((run) => run.status === 'approved' && run.cycle === 'monthly');
  const shownRuns = runs.filter(
    (run) => run.status !== 'approved' || run === latestWeekly || run === latestMonthly,
  );
  const earlierRuns = runs.filter((run) => !shownRuns.includes(run));

  const renderRun = (run: RunSummary) => {
      const approved = run.status === 'approved';
      return (
        <div className="row" style={{ flexWrap: 'wrap' }} key={run.id}>
          <div style={{ flex: 1, minWidth: 170 }}>
            <div className="name">
              {run.cycle === 'monthly' ? 'Monthly · ' : 'Weekly · '}
              {formatDate(run.period_from)} – {formatDate(run.period_to)}
            </div>
            <div className="sub">
              {run.pay_on && `pay day ${formatDate(run.pay_on)} · `}
              {run.worker_count} employees · {formatRs(run.net_rs)} cash
              {run.loan_rs > 0 && <> · loans repaid {formatRs(run.loan_rs)}</>}
              {run.epf_employee_rs + run.epf_employer_rs + run.etf_rs > 0 && (
                <>
                  {' · '}EPF {formatRs(run.epf_employee_rs + run.epf_employer_rs)} · ETF{' '}
                  {formatRs(run.etf_rs)}
                </>
              )}{' '}
              · by {run.created_by_name}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {approved ? (
              <span className="pill p-ok">Approved</span>
            ) : (
              <span className="pill p-wait">Unapproved</span>
            )}

            {/* The full pack prints either way — it is what the owner reads
                before approving. */}
            <button
              type="button"
              className="btn sm"
              disabled={busy === run.id}
              onClick={() => void print(run.id, false)}
            >
              Report
            </button>

            <button
              type="button"
              className="btn sm"
              disabled={busy === run.id}
              onClick={() => void printReceipts(run.id)}
            >
              Receipts
            </button>

            {/* Pay goes out once he has approved: each man signs for it. */}
            {approved && (
              <button
                type="button"
                className={`btn sm${handing === run.id ? ' pri' : ''}`}
                onClick={() => setHanding(handing === run.id ? null : run.id)}
              >
                {handing === run.id ? 'Hide' : 'Hand out pay'}
              </button>
            )}

            {/* Standalone payslips only once he has approved. */}
            {approved && (
              <button
                type="button"
                className="btn sm pri"
                disabled={busy === run.id}
                onClick={() => void print(run.id, true)}
              >
                Payslips
              </button>
            )}

            {isOwner && !approved && (
              <button
                type="button"
                className="btn sm pri"
                disabled={busy === run.id}
                onClick={() => setReviewing(reviewing === run.id ? null : run.id)}
              >
                {reviewing === run.id ? 'Hide summary' : 'Review & approve'}
              </button>
            )}
            {isOwner && approved && (
              <button
                type="button"
                className="btn sm"
                disabled={busy === run.id}
                onClick={() => void act(run.id, () => payrunApi.unapprove(run.id))}
              >
                Unapprove
              </button>
            )}

            {/* No confirm() — an inline confirmation instead. */}
            {isOwner && !approved && (
              confirming === run.id ? (
                <>
                  <span className="sub">Delete it?</span>
                  <button
                    type="button"
                    className="btn sm dang"
                    disabled={busy === run.id}
                    onClick={() => void act(run.id, () => payrunApi.remove(run.id))}
                  >
                    Yes, delete
                  </button>
                  <button type="button" className="btn sm" onClick={() => setConfirming(null)}>
                    Keep it
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn sm dang"
                  onClick={() => setConfirming(run.id)}
                >
                  Delete
                </button>
              )
            )}
          </div>
          {handing === run.id && (
            <div style={{ flexBasis: '100%' }}>
              <HandOut runId={run.id} />
            </div>
          )}
          {/* Approving is only at the foot of the summary — never unseen. */}
          {reviewing === run.id && (
            <div style={{ flexBasis: '100%' }}>
              <ReviewRun
                runId={run.id}
                userName={userName}
                onApproved={async () => { setReviewing(null); await load(); }}
              />
            </div>
          )}
        </div>
      );
  };

  return (
    <div className="card">
      <h3>Pay runs</h3>
      <div className="sub">
        Every week that has been run. Dates already paid cannot be run again.
      </div>

      {error && <div className="banner b-no">{error}</div>}
      {runs.length === 0 && <div className="empty">No pay runs yet.</div>}

      {shownRuns.map(renderRun)}

      {earlierRuns.length > 0 && (
        <>
          <button
            type="button"
            className="btn sm"
            style={{ marginTop: 10, width: '100%', justifyContent: 'flex-start' }}
            aria-expanded={showEarlier}
            onClick={() => setShowEarlier(!showEarlier)}
          >
            {showEarlier ? '▾' : '▸'} Earlier pay runs ({earlierRuns.length})
          </button>
          {showEarlier && earlierRuns.map(renderRun)}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------ hand out pay

/**
 * Handing out an approved run's pay: every employee in it, what he is handed,
 * and whether he has signed for it on the tablet. His signature goes on his
 * payslip ("Received by") and in the pack's signature column. Added
 * 25 September 2026 — "sign even the payslips on the touch screen".
 */
function HandOut({ runId }: { runId: string }) {
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [signing, setSigning] = useState<RunRow | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await payrunApi.get(runId);
      setDetail(result);
      setError('');
      return result;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that pay run.');
      return null;
    }
  }, [runId]);

  useEffect(() => { void load(); }, [load]);

  if (!detail) return <div className="empty">{error || 'Loading…'}</div>;

  const signedCount = detail.rows.filter((row) => row.signed).length;
  const printOne = (row: RunRow) =>
    openPrint(
      `Payslip — ${row.worker_name}`,
      <Payslips detail={{ ...detail, rows: [row] }} />,
    );

  return (
    <div
      style={{
        background: 'var(--surface-2)', borderRadius: 12,
        padding: '10px 12px', margin: '6px 0 4px',
      }}
    >
      <div className="sub" style={{ marginBottom: 4 }}>
        Hand each man his pay and have him sign for it. {signedCount} of {detail.rows.length} signed.
      </div>
      {error && <div className="banner b-no">{error}</div>}

      {detail.rows.map((row) => (
        <div className="row" key={row.id} style={{ flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 150 }}>
            <div className="name">{row.worker_name}</div>
            <div className="sub">
              {row.signed
                ? `signed ${row.signed_at ? new Date(row.signed_at).toLocaleString('en-GB') : ''}`
                : <span style={{ color: 'var(--text-danger)' }}>not signed yet</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="name" style={{ minWidth: 90, textAlign: 'right' }}>{formatRs(handedRs(row.take_home_rs))}</span>
            <button
              type="button"
              className={`btn sm${row.signed ? '' : ' pri'}`}
              onClick={() => setSigning(row)}
            >
              {row.signed ? 'Signature' : 'Sign for pay'}
            </button>
            <button type="button" className="btn sm" onClick={() => printOne(row)}>
              Payslip
            </button>
          </div>
        </div>
      ))}

      {signing && (
        <SignDialog
          title="Pay received — signature"
          lead="Hand over his pay and have him sign on the tablet."
          summary={
            <>
              <div className="row" style={{ paddingTop: 0 }}>
                <div>
                  <div className="name">{signing.worker_name}</div>
                  <div className="sub">{signing.roles_label}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="name" style={{ fontSize: 22 }}>{formatRs(handedRs(signing.take_home_rs))}</div>
                  <div className="sub">
                    {formatDate(detail.run.period_from)} – {formatDate(detail.run.period_to)}
                  </div>
                </div>
              </div>
              <div className="sub" style={{ marginTop: 2 }}>
                Rupees {rupeesInWords(handedRs(signing.take_home_rs))} only
                {shortRs(signing.take_home_rs) > 0 &&
                  ` · short ${formatRs(shortRs(signing.take_home_rs))}, carried to his next pay`}
              </div>
            </>
          }
          padLabel={`Signature of ${signing.worker_name}`}
          signedPath={
            signing.signed
              ? payrunApi.payslipSignaturePath(runId, signing.worker_id, signing.signed_at)
              : null
          }
          signedAt={signing.signed_at}
          onSave={async (png) => {
            await payrunApi.signPayslip(runId, signing.worker_id, png);
            const fresh = await load();
            setSigning(fresh?.rows.find((row) => row.worker_id === signing.worker_id) ?? null);
          }}
          printLabel="Print payslip"
          onPrint={() => { printOne(signing); setSigning(null); }}
          onClose={() => setSigning(null)}
        />
      )}
    </div>
  );
}
